import React, { useEffect, useMemo, useState } from 'react';
import { RatingBadge } from '../../../../shared/RatingBadge.js';
import Stepper from '../../../../shared/Stepper.js';
import { simulateEcobagSavings } from './ecobagCalculations.js';
import { formatPaybackYears as formatPayback } from '../../calculations/profitability.js';

const LITERS_SHORTCUTS = [1, 2, 3];

const STEP_LABELS = [
  { label: 'Volume' },
  { label: 'WC' },
  { label: 'Résultats' },
];

/**
 * 3-step wizard: liters removed per flush → WC selection (excludes built-in WCs,
 * shown but non-selectable) → results. No rebound step — a flush-volume reduction
 * has no analogous "user compensates by running it longer" behavior.
 */
const EcobagSubModule = ({ equipments, usages, waterPricing, onResultChange }) => {
  const [selectedIds, setSelectedIds] = useState(new Set());
  const [litersRemoved, setLitersRemoved] = useState('');

  const [currentStep, setCurrentStep] = useState(0); // 0=volume, 1=WC, 2=résultats
  const [furthestStepReached, setFurthestStepReached] = useState(0);
  const [filtersOpen, setFiltersOpen] = useState(false);
  const [sectionFilter, setSectionFilter] = useState([]);

  const wcEquipments = useMemo(
    () => equipments.filter(e => e.type_equipment === 'wc'),
    [equipments]
  );

  const eligibleEquipments = useMemo(
    () => wcEquipments.filter(e => !e.technical_data?.built_in),
    [wcEquipments]
  );

  const ineligibleEquipments = useMemo(
    () => wcEquipments.filter(e => e.technical_data?.built_in),
    [wcEquipments]
  );

  const sections = useMemo(() => Array.from(
    new Map(eligibleEquipments.map(e => [e.location?.section_id, e.location?.name_section || e.location?.section_id])).entries()
  ).map(([value, label]) => ({ value, label })), [eligibleEquipments]);

  const visibleEquipments = useMemo(
    () => eligibleEquipments.filter(e => sectionFilter.length === 0 || sectionFilter.includes(e.location?.section_id)),
    [eligibleEquipments, sectionFilter]
  );

  const selectedEquipments = useMemo(
    () => eligibleEquipments.filter(e => selectedIds.has(e._id)),
    [eligibleEquipments, selectedIds]
  );

  const parsedLitersRemoved = litersRemoved === '' ? null : parseFloat(litersRemoved);

  const simulationResult = useMemo(
    () => simulateEcobagSavings({
      equipments: selectedEquipments,
      usages,
      litersRemoved: parsedLitersRemoved,
      waterPricing,
    }),
    [selectedEquipments, usages, parsedLitersRemoved, waterPricing]
  );

  useEffect(() => {
    onResultChange?.(simulationResult);
  }, [simulationResult, onResultChange]);

  const canAdvance = [
    parsedLitersRemoved != null && parsedLitersRemoved > 0, // step 0: liters
    selectedIds.size > 0,                                    // step 1: WC selection
    false,                                                   // step 2: terminal
  ][currentStep];

  const goToStep = (index) => {
    if (index <= furthestStepReached) setCurrentStep(index);
  };

  const goNext = () => {
    if (!canAdvance || currentStep === STEP_LABELS.length - 1) return;
    const next = currentStep + 1;
    setCurrentStep(next);
    setFurthestStepReached(f => Math.max(f, next));
  };

  const goPrevious = () => {
    if (currentStep === 0) return;
    setCurrentStep(s => s - 1);
  };

  const toggleRow = (id) => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const allVisibleSelected = visibleEquipments.length > 0 && visibleEquipments.every(e => selectedIds.has(e._id));
  const toggleSelectAllVisible = () => {
    setSelectedIds(prev => {
      const next = new Set(prev);
      visibleEquipments.forEach(e => allVisibleSelected ? next.delete(e._id) : next.add(e._id));
      return next;
    });
  };

  const toggleSectionFilter = (value) => {
    setSectionFilter(prev => prev.includes(value) ? prev.filter(v => v !== value) : [...prev, value]);
  };

  const renderVolumeStep = () => (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">Écobags</span>
          <h2 className="apv2-step-heading">Volume retiré par chasse</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {STEP_LABELS.length}</span>
      </div>
      <div className="apv2-step-body">
        <div className="apv2-step-explanation">
          <span className="apv2-step-explanation-icon">💡</span>
          <p className="apv2-step-explanation-text">Indiquez le volume que l'écobag retirera à chaque chasse d'eau.</p>
        </div>
        <div className="apv2-flow-input-row">
          <label className="apv2-flow-label" htmlFor="ecobag-liters-removed">Volume retiré (L)</label>
          <input
            id="ecobag-liters-removed"
            type="number"
            min="0"
            step="0.5"
            className="apv2-flow-input"
            value={litersRemoved}
            onChange={e => setLitersRemoved(e.target.value)}
          />
          <div className="apv2-flow-shortcuts">
            {LITERS_SHORTCUTS.map(v => (
              <button key={v} type="button" className="ceq-chip" onClick={() => setLitersRemoved(String(v))}>
                {v} L
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
          Suivant →
        </button>
      </div>
    </div>
  );

  const renderWcStep = () => (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">Écobags</span>
          <h2 className="apv2-step-heading">Choix des toilettes</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {STEP_LABELS.length}</span>
      </div>
      <div className="apv2-step-body">
        <div className="apv2-step-explanation">
          <span className="apv2-step-explanation-icon">💡</span>
          <p className="apv2-step-explanation-text">
            Seules les toilettes non encastrées permettent d'accéder au réservoir pour y installer un écobag.
          </p>
        </div>

        <div className="apv2-filter-toggle-row">
          <button type="button" className="apv2-filter-toggle-btn" onClick={() => setFiltersOpen(o => !o)}>
            {filtersOpen ? '▾' : '▸'} Filtrer
            {sectionFilter.length > 0 && <span className="apv2-filter-toggle-count">{sectionFilter.length}</span>}
          </button>
        </div>

        {filtersOpen && (
          <div className="apv2-filter-panel">
            <div className="apv2-filter-panel-row">
              <span className="apv2-filter-panel-label">Section</span>
              {sections.map(s => (
                <button key={s.value} type="button"
                  className={`ceq-chip${sectionFilter.includes(s.value) ? ' ceq-chip--active' : ''}`}
                  onClick={() => toggleSectionFilter(s.value)}>
                  {s.label}
                </button>
              ))}
            </div>
          </div>
        )}

        <div className="apv2-flat-table-wrapper">
          <table className="apv2-flat-table">
            <thead>
              <tr>
                <th className="apv2-flat-table-th-check">
                  <input type="checkbox" checked={allVisibleSelected} onChange={toggleSelectAllVisible} aria-label="Tout sélectionner" />
                </th>
                <th>Note</th>
                <th>Section</th>
                <th>Salle</th>
                <th>Volume actuel</th>
              </tr>
            </thead>
            <tbody>
              {visibleEquipments.map(equipment => {
                const isSelected = selectedIds.has(equipment._id);
                return (
                  <tr
                    key={equipment._id}
                    className={isSelected ? 'apv2-flat-table-row--selected' : ''}
                    onClick={() => toggleRow(equipment._id)}
                  >
                    <td className="apv2-flat-table-td-check">
                      <input type="checkbox" checked={isSelected} onChange={() => toggleRow(equipment._id)} onClick={e => e.stopPropagation()} />
                    </td>
                    <td><RatingBadge letter={equipment.rating_letter} /></td>
                    <td>{equipment.location?.name_section || '—'}</td>
                    <td>{equipment.location?.name_room || '—'}</td>
                    <td>{equipment.water_value ?? '—'} {equipment.water_value_unit || ''}</td>
                  </tr>
                );
              })}
              {ineligibleEquipments.map(equipment => (
                <tr key={equipment._id} className="apv2-flat-table-row--ineligible">
                  <td className="apv2-flat-table-td-check">
                    <input type="checkbox" checked={false} disabled />
                  </td>
                  <td><RatingBadge letter={equipment.rating_letter} /></td>
                  <td>{equipment.location?.name_section || '—'}</td>
                  <td>{equipment.location?.name_room || '—'}</td>
                  <td>
                    {equipment.water_value ?? '—'} {equipment.water_value_unit || ''}
                    <span className="apv2-ecobag-ineligible-badge" title="Toilette encastrée : réservoir non accessible">
                      Toilette encastrée — non éligible
                    </span>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <button type="button" className="apv2-footer-btn-cta" onClick={goNext} disabled={!canAdvance}>
          Suivant →
        </button>
      </div>
    </div>
  );

  const renderResultsStep = () => (
    <div className="apv2-step-card">
      <div className="apv2-step-header">
        <div className="apv2-step-header-text">
          <span className="apv2-step-eyebrow">Écobags</span>
          <h2 className="apv2-step-heading">Résultats</h2>
        </div>
        <span className="apv2-step-counter">{currentStep + 1} / {STEP_LABELS.length}</span>
      </div>
      <div className="apv2-step-body">
      {!simulationResult.ready ? (
        <p className="apv2-sim-hint">Complétez les étapes précédentes pour afficher les résultats détaillés.</p>
      ) : (
        <>
          <div className="apv2-summary-cards">
            <div className="apv2-summary-card">
              <span className="apv2-summary-value">{simulationResult.totalSavingsM3.toFixed(1)} m³</span>
              <span className="apv2-summary-label">économisés / an</span>
            </div>
            <div className="apv2-summary-card">
              <span className="apv2-summary-value">
                {simulationResult.waterPriceAvailable
                  ? `${simulationResult.savingsEurTotalPerYear.toFixed(0)} €`
                  : 'N/A'}
              </span>
              <span className="apv2-summary-label">
                {simulationResult.waterPriceAvailable ? 'économisés / an' : 'prix de l\'eau non renseigné'}
              </span>
              {simulationResult.waterPriceAvailable && (
                <div className="apv2-summary-breakdown">
                  <span>dont {simulationResult.savingsEurWaterPerYear.toFixed(0)} € eau</span>
                  {simulationResult.heatingPriceAvailable ? (
                    <span>dont {simulationResult.savingsEurHeatingPerYear.toFixed(0)} € chauffage</span>
                  ) : (
                    <span className="apv2-summary-breakdown-warning">Coût du chauffage non calculable — prix de l'énergie non renseigné</span>
                  )}
                </div>
              )}
            </div>
            <div className="apv2-summary-card">
              <span className="apv2-summary-value">{simulationResult.savingsPct.toFixed(0)} %</span>
              <span className="apv2-summary-label">de réduction</span>
            </div>
            <div className="apv2-summary-card apv2-summary-card--secondary">
              <span className="apv2-summary-value">{simulationResult.totalCurrentM3.toFixed(1)} m³</span>
              <span className="apv2-summary-label">consommation actuelle sélectionnée</span>
            </div>
            <div className="apv2-summary-card apv2-summary-card--secondary">
              <span className="apv2-summary-value">{simulationResult.totalInvestmentEur.toFixed(0)} €</span>
              <span className="apv2-summary-label">investissement total</span>
            </div>
            <div className="apv2-summary-card apv2-summary-card--secondary">
              <span className="apv2-summary-value">
                {simulationResult.paybackYears != null ? formatPayback(simulationResult.paybackYears) : 'N/A'}
              </span>
              <span className="apv2-summary-label">retour sur investissement</span>
            </div>
          </div>

          <div className="apv2-results-table-wrapper">
            <table className="apv2-results-table">
              <thead>
                <tr>
                  <th>Point d'eau</th>
                  <th>Volume / chasse</th>
                  <th>Consommation (m³/an)</th>
                  <th className="apv2-th-right">Économie</th>
                </tr>
              </thead>
              <tbody>
                {(() => {
                  const MIN_BAR_PCT = 18;
                  const MAX_BAR_PCT = 100;
                  const maxSqrtM3 = Math.max(1e-9, ...simulationResult.perEquipment.map(r => Math.sqrt(r.currentM3)));

                  const sortedResults = [...simulationResult.perEquipment].sort((a, b) => b.currentM3 - a.currentM3);

                  return sortedResults.map(result => {
                    const equipment = selectedEquipments.find(e => e._id === result.equipmentId);
                    const hasConsumption = result.currentM3 > 0;

                    const totalBarPct = MIN_BAR_PCT + (Math.sqrt(result.currentM3) / maxSqrtM3) * (MAX_BAR_PCT - MIN_BAR_PCT);
                    const newShareOfTotal = hasConsumption ? result.newM3 / result.currentM3 : 0;
                    const filledShare = newShareOfTotal * 100;
                    const savedShare = 100 - filledShare;

                    return (
                      <tr key={result.equipmentId}>
                        <td>
                          <div className="apv2-results-location">{equipment?.location?.name_section || '—'}</div>
                          <div className="apv2-results-sublabel">{equipment?.location?.name_room || '—'}</div>
                        </td>
                        <td className="apv2-results-flow">
                          {result.currentFlow} <span className="apv2-results-arrow">→</span> {result.targetFlow.toFixed(1)}
                          <span className="apv2-results-unit"> {equipment?.water_value_unit}</span>
                        </td>
                        <td>
                          {hasConsumption ? (
                            <>
                              <div className="apv2-savings-bar-track" style={{ width: `${totalBarPct}%` }}>
                                <div className="apv2-savings-bar-fill" style={{ width: `${filledShare}%` }} />
                                {savedShare > 0 && (
                                  <div className="apv2-savings-bar-saved" style={{ width: `${savedShare}%` }} title={`${result.savingsM3.toFixed(1)} m³/an économisés`} />
                                )}
                              </div>
                              <div className="apv2-results-sublabel">
                                {result.currentM3.toFixed(1)} <span className="apv2-results-arrow">→</span> {result.newM3.toFixed(1)} m³
                              </div>
                            </>
                          ) : (
                            <div className="apv2-results-sublabel apv2-results-sublabel--muted">Pas de consommation significative</div>
                          )}
                        </td>
                        <td className="apv2-th-right">
                          <div className="apv2-results-savings-pct">-{result.savingsPct.toFixed(0)} %</div>
                          <div className="apv2-results-sublabel">-{result.savingsM3.toFixed(1)} m³/an</div>
                          {result.hasNonReducibleUsage && (
                            <span className="apv2-non-reducible-badge" title="Contient un usage à volume fixe, non affecté par le volume de chasse">
                              Volume fixe
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  });
                })()}
              </tbody>
            </table>
          </div>
        </>
      )}
      </div>
      <div className="apv2-step-footer">
        <button type="button" className="apv2-footer-btn-ghost" onClick={goPrevious} disabled={currentStep === 0}>
          ← Précédent
        </button>
        <span />
      </div>
    </div>
  );

  const STEP_RENDERERS = [renderVolumeStep, renderWcStep, renderResultsStep];

  return (
    <div>
      <div className="apv2-wizard-shell">
        <h2 className="apv2-wizard-title">Écobags</h2>
        <div className="apv2-wizard-nav">
          <button type="button" className="apv2-wizard-nav-btn" onClick={goPrevious} disabled={currentStep === 0}>
            ← Précédent
          </button>
          <Stepper
            steps={STEP_LABELS}
            currentStep={currentStep}
            furthestStepReached={furthestStepReached}
            onStepClick={goToStep}
          />
          <button type="button" className="apv2-wizard-nav-btn" onClick={goNext} disabled={!canAdvance || currentStep === STEP_LABELS.length - 1}>
            Suivant →
          </button>
        </div>

        <div className={`apv2-persistent-summary ${!simulationResult.ready ? 'apv2-persistent-summary--empty' : ''}`}>
          {simulationResult.ready ? (
            <>
              <div className="apv2-persistent-summary-stat">
                <span className="apv2-persistent-summary-value">{simulationResult.totalSavingsM3.toFixed(1)} m³</span>
                <span className="apv2-persistent-summary-label">économisés / an</span>
              </div>
              <div className="apv2-persistent-summary-stat">
                <span className="apv2-persistent-summary-value">{simulationResult.waterPriceAvailable ? `${simulationResult.savingsEurTotalPerYear.toFixed(0)} €` : 'N/A'}</span>
                <span className="apv2-persistent-summary-label">économisés / an</span>
              </div>
              <div className="apv2-persistent-summary-stat">
                <span className="apv2-persistent-summary-value">{simulationResult.savingsPct.toFixed(0)} %</span>
                <span className="apv2-persistent-summary-label">de réduction</span>
              </div>
              <div className="apv2-persistent-summary-stat">
                <span className="apv2-persistent-summary-value">{simulationResult.paybackYears != null ? formatPayback(simulationResult.paybackYears) : 'N/A'}</span>
                <span className="apv2-persistent-summary-label">retour sur investissement</span>
              </div>
            </>
          ) : (
            <span className="apv2-persistent-summary-empty-text">Renseignez le volume retiré et sélectionnez des toilettes pour voir l'estimation.</span>
          )}
        </div>
      </div>

      {STEP_RENDERERS[currentStep]()}
    </div>
  );
};

export default EcobagSubModule;
